export interface RuntimeVersionMetadata {
  service: string;
  version: string;
  commit: string;
  branch: string;
  buildTime: string | null;
  environment: string;
  deploymentId: string;
  dirty: boolean;
  pipelineId?: string;
}

export const DEV_FALLBACK_METADATA: RuntimeVersionMetadata = {
  service: 'ledger-one-app',
  version: '0.0.0',
  commit: 'development',
  branch: 'local',
  buildTime: null,
  environment: 'local',
  deploymentId: 'local-local-development',
  dirty: false,
};

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

export function isValidRuntimeVersionMetadata(
  value: unknown
): value is RuntimeVersionMetadata {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const data = value as Record<string, unknown>;
  return (
    typeof data['commit'] === 'string' &&
    typeof data['environment'] === 'string' &&
    typeof data['branch'] === 'string' &&
    typeof data['dirty'] === 'boolean'
  );
}

export function parseRuntimeVersionMetadata(
  raw: unknown
): RuntimeVersionMetadata | null {
  if (!isValidRuntimeVersionMetadata(raw)) {
    return null;
  }

  const data = raw as unknown as Record<string, unknown>;
  const buildTime =
    data['buildTime'] === null
      ? null
      : typeof data['buildTime'] === 'string'
        ? data['buildTime']
        : null;

  const metadata: RuntimeVersionMetadata = {
    service: asString(data['service'], DEV_FALLBACK_METADATA.service),
    version: asString(data['version'], DEV_FALLBACK_METADATA.version),
    commit: asString(data['commit'], DEV_FALLBACK_METADATA.commit),
    branch: asString(data['branch'], DEV_FALLBACK_METADATA.branch),
    buildTime,
    environment: asString(data['environment'], DEV_FALLBACK_METADATA.environment),
    deploymentId: asString(
      data['deploymentId'],
      DEV_FALLBACK_METADATA.deploymentId
    ),
    dirty: data['dirty'] === true,
  };

  if (typeof data['pipelineId'] === 'string' && data['pipelineId'].trim()) {
    metadata.pipelineId = data['pipelineId'].trim();
  }

  return metadata;
}

/** Show dirty warning only for local/dev diagnostics (never QA/prod). */
export function shouldShowDirtyWarning(metadata: RuntimeVersionMetadata): boolean {
  if (!metadata.dirty) {
    return false;
  }
  const env = metadata.environment.toLowerCase();
  return env === 'local' || env === 'dev' || env === 'development';
}
